/**
 * Budget Pacing: "Automated pacing: On/Off" for the selected client (46062138).
 *
 * Off leaves the client out of the Monday and Thursday automatic budget
 * changes, from the next run; nothing is back-filled. The client stays on this
 * page (numbers, cycle, history) and manual budget changes still work. Staff
 * only: hidden for anyone who is not internal Ads staff, and hidden if the
 * backend answers 403 (an "expert" is internal here but not an operator there).
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { canAccessInternalAdsTools } from '@/lib/adsToolsAccess';
import log from '@/lib/logger';
import {
  getAutomatedPacing,
  updateAutomatedPacing,
  type AutomatedPacingState,
} from '@/services/googleAdsMonthlyService';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';

export const AUTOMATED_PACING_QUERY_KEY = 'google-ads-automated-pacing';

function statusOf(err: unknown): number | undefined {
  return (err as { response?: { status?: number } })?.response?.status;
}

function changedOn(iso?: string | null): string {
  if (!iso) return 'an unknown date';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'an unknown date';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function changedLine(state: AutomatedPacingState, verb: string): string | null {
  if (!state.changed_by && !state.changed_at) return null;
  return `${verb} by ${state.changed_by ?? 'unknown'} on ${changedOn(state.changed_at)}.`;
}

export function AutomatedPacingSwitch({ tenantId }: { tenantId: string }) {
  const { userProfile } = useWorkspaceStore();
  const staff = canAccessInternalAdsTools(userProfile);
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');

  const query = useQuery({
    queryKey: [AUTOMATED_PACING_QUERY_KEY, tenantId],
    queryFn: () => getAutomatedPacing({ tenantId }),
    enabled: staff && Boolean(tenantId),
    retry: false,
  });

  const save = useMutation({
    mutationFn: (enabled: boolean) =>
      updateAutomatedPacing({ tenantId, enabled, reason: enabled ? null : reason.trim() || null }),
    onSuccess: (next) => {
      queryClient.setQueryData([AUTOMATED_PACING_QUERY_KEY, tenantId], next);
      log.info('[AutomatedPacing] saved', { tenantId, enabled: next.enabled });
      toast.success(
        next.enabled
          ? 'Automated pacing is on again for this client from the next run.'
          : 'This client is left out of automated pacing from the next run.',
      );
      setConfirming(false);
      setReason('');
    },
    onError: (err) => {
      log.warn('[AutomatedPacing] save failed', { tenantId, status: statusOf(err) });
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not save the automated pacing setting. Try again.');
    },
  });

  if (!staff) return null;
  if (query.isError) {
    if (statusOf(query.error) !== 403) {
      log.warn('[AutomatedPacing] read failed', { tenantId, status: statusOf(query.error) });
    }
    return null;
  }
  const state = query.data;
  if (!state) return null;

  const turningOff = state.enabled;
  const onLine = changedLine(state, 'Turned back on');
  return (
    <div
      className="flex flex-wrap items-start justify-between gap-3 rounded-md border px-4 py-3 text-sm"
      data-testid="automated-pacing-switch"
    >
      <div className="space-y-1">
        {state.enabled ? (
          <>
            <p className="font-medium">Automated pacing: On</p>
            <p className="text-muted-foreground">
              The Monday and Thursday automatic budget changes include this client.
              {onLine ? ` ${onLine}` : ''}
            </p>
          </>
        ) : (
          <div data-testid="automated-pacing-off" className="space-y-1">
            <p className="font-medium text-coral-dark">Automated pacing is off for this client.</p>
            <p className="text-muted-foreground">
              The Monday and Thursday automatic budget changes leave it out. Manual budget changes
              on this page still work. {changedLine(state, 'Turned off')}
              {state.reason ? ` Reason: ${state.reason}` : ''}
            </p>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Staff only</span>
        <Switch
          aria-label="Automated pacing for this client"
          checked={state.enabled}
          disabled={save.isPending}
          onCheckedChange={() => setConfirming(true)}
        />
      </div>
      <AlertDialog
        open={confirming}
        onOpenChange={(open) => {
          if (!save.isPending) setConfirming(open);
        }}
      >
        <AlertDialogContent role="alertdialog" aria-modal="true">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {turningOff ? 'Turn off automated pacing?' : 'Turn on automated pacing?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {turningOff
                ? `Leave ${tenantId} out of the Monday and Thursday automatic budget changes, from the next run. Its numbers stay on this page and manual budget changes still work.`
                : `Include ${tenantId} in the Monday and Thursday automatic budget changes again, from the next run. Nothing is changed for past runs.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {turningOff ? (
            <label className="block space-y-1 text-sm">
              <span>Reason (optional)</span>
              <Textarea
                value={reason}
                maxLength={500}
                onChange={(e) => setReason(e.target.value)}
                placeholder="For example: the owner asked us to hold the budget"
              />
            </label>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={save.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={save.isPending}
              onClick={(e) => {
                e.preventDefault();
                save.mutate(!state.enabled);
              }}
            >
              {turningOff ? 'Turn off automated pacing' : 'Turn on automated pacing'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
